import { barStyleFor } from '../timeline/barStyle.js';
import {
  type Axis,
  type DragAbilities,
  type ScheduleView,
  allowedAxes,
  canResize,
  stepFor,
} from '../timeline/dropLogic.js';
import type { ScheduleBar } from '../types.js';

/**
 * 329 (FR-008, FR-009, research R1) — what the Guide says each bar can do, and its first line.
 *
 * 🔴 **Derived, never hand-written.** Each row is one sample bar asked of the drag rules themselves
 * (`allowedAxes`, `canResize`), so a rule change changes the Guide's words with it. A hand-kept table
 * is what 096's legend comment warns against: it drifts the moment a rule moves, and silently.
 *
 * The samples ask with full abilities: the table describes the bars, and the first line
 * (`viewerLine`) says what THIS viewer may do.
 */

export type DragPhrase = 'diagonal' | 'car-only' | 'time-only' | 'none';

export const DRAG_WORDS: Record<DragPhrase, string> = {
  diagonal: 'Another car and another time',
  'car-only': 'Another car only',
  'time-only': 'Same car, other times',
  none: 'Can’t be moved',
};

export type MoveRuleKind =
  | 'awaiting_payment'
  | 'confirmed'
  | 'extended_rental'
  | 'checked_out'
  | 'checked_in'
  | 'block'
  | 'completed'
  | 'finished_paid'
  | 'cancelled'
  | 'abandoned_unpaid'
  | 'abandoned_no_booking'
  | 'legacy_extension';

export interface MoveRule {
  kind: MoveRuleKind;
  label: string;
  note?: string;
  drag: DragPhrase;
  phrase: string;
  resize: boolean;
  /** The bar's own class string (`barStyle.ts`), so the row's swatch looks like the bar. */
  swatch: string;
}

function sample(overrides: Partial<ScheduleBar>): ScheduleBar {
  return {
    id: 'guide-sample',
    car_id: 'guide-car',
    start_at: '2026-01-01T02:00:00.000Z',
    end_at: '2026-01-01T04:00:00.000Z',
    kind: 'booking',
    state: 'committed',
    booking_id: 'guide-booking',
    booking_status: 'confirmed',
    overdue: false,
    reason: null,
    continues_before: false,
    continues_after: false,
    group_id: null,
    money_settled: false,
    root_booking_id: null,
    has_extension: false,
    ...overrides,
  };
}

/** One sample per row. A confirmed booking is paid, so it is sampled settled, as it is on screen. */
export const MOVE_RULE_SAMPLES: Record<MoveRuleKind, ScheduleBar> = {
  awaiting_payment: sample({ state: 'tentative', booking_status: 'awaiting_payment' }),
  confirmed: sample({ money_settled: true }),
  extended_rental: sample({ money_settled: true, root_booking_id: 'guide-root' }),
  checked_out: sample({ booking_status: 'checked_out', money_settled: true }),
  checked_in: sample({ booking_status: 'checked_in', money_settled: true }),
  block: sample({
    kind: 'manual_block',
    state: 'non_booking',
    booking_id: null,
    booking_status: null,
  }),
  completed: sample({ booking_status: 'completed', money_settled: false }),
  // 379: judged after its end (see `JUDGED_AT_END`): a finished, paid rental moves as a correction.
  finished_paid: sample({ booking_status: 'completed', money_settled: true }),
  cancelled: sample({ booking_status: 'cancelled', state: 'tentative' }),
  abandoned_unpaid: sample({ state: 'likely_abandoned', booking_status: 'awaiting_payment' }),
  abandoned_no_booking: sample({
    state: 'likely_abandoned',
    booking_id: null,
    booking_status: null,
  }),
  legacy_extension: sample({ kind: 'extension', state: 'unheld' }),
};

const LABELS: Record<MoveRuleKind, { label: string; note?: string }> = {
  awaiting_payment: { label: 'Awaiting payment' },
  confirmed: { label: 'Confirmed' },
  extended_rental: {
    label: 'Extended rental',
    note: 'Any leg moves the whole rental, until the car is collected.',
  },
  checked_out: { label: 'Checked out' },
  checked_in: { label: 'Checked in' },
  block: { label: 'Block', note: 'Blocked, maintenance, inspection and other.' },
  completed: { label: 'Completed', note: 'Unpaid. A paid one moves as a finished rental, below.' },
  finished_paid: {
    label: 'Finished and paid',
    note: 'Completed or checked in, after its return: a correction of what happened. A reason is required, the price stays, the customer is not told.',
  },
  cancelled: { label: 'Cancelled' },
  abandoned_unpaid: { label: 'Abandoned?, still awaiting payment' },
  abandoned_no_booking: { label: 'Abandoned?, booking gone' },
  legacy_extension: { label: 'Older extension hold' },
};

const FULL: DragAbilities = { canMoveBooking: true, canEditBlocks: true };
const JUDGED_AT_END = new Set<MoveRuleKind>(['finished_paid']);

function dragOf(axes: Axis[]): DragPhrase {
  const row = axes.includes('row');
  const time = axes.includes('time');
  if (row && time) return 'diagonal';
  if (row) return 'car-only';
  if (time) return 'time-only';
  return 'none';
}

export function moveRules(): MoveRule[] {
  return (Object.keys(MOVE_RULE_SAMPLES) as MoveRuleKind[]).map((kind) => {
    const bar = MOVE_RULE_SAMPLES[kind];
    // Every sample is judged while it is in progress, except the finished one, judged after its end (379).
    const at = JUDGED_AT_END.has(kind) ? Date.parse(bar.end_at) : Date.parse(bar.start_at);
    const drag = dragOf(allowedAxes(bar, FULL, at));
    return {
      kind,
      ...LABELS[kind],
      drag,
      phrase: DRAG_WORDS[drag],
      resize: canResize(bar, FULL, 'day'),
      swatch: barStyleFor(bar).className,
    };
  });
}

function stepWords(view: ScheduleView): string {
  const minutes = stepFor(view) / 60_000;
  if (minutes === 24 * 60) return 'whole days';
  if (minutes % 60 === 0) return minutes === 60 ? '1 hour' : `${minutes / 60} hours`;
  return `${minutes} minutes`;
}

/** How far a time move snaps in each view (spec edge case: Month), from the drag's own step. */
export function stepSentence(): string {
  return `A time move snaps to ${stepWords('day')} in Day, ${stepWords('week')} in Week and ${stepWords('month')} in Month.`;
}

/**
 * The Guide's first line (research R2), from the two abilities the page already computes. Moving
 * bookings is `manage Booking`, which in practice only Admin holds, alongside block editing.
 */
export function viewerLine(abilities: DragAbilities): string {
  if (abilities.canMoveBooking) return 'You can move bookings and blocks.';
  if (abilities.canEditBlocks) {
    return 'You can move and add blocks on your cars. Bookings are moved by an Admin, or by you from the booking page (Reschedule).';
  }
  return 'This schedule is read-only for you. To change a booking’s time, open it and use Reschedule.';
}
